/**
 * WAV helpers.
 *
 * Hamsa's STT rejects raw PCM (HTTP 400, code 12400) — a RIFF header is required.
 * Its own TTS, meanwhile, returns a WAV whose RIFF and data sizes are 0xFFFFFFFF
 * placeholders, so anything that trusts the header (ffmpeg, browsers, Node's wav
 * readers) reads a 2 second clip as 134,217 seconds. Never trust the header;
 * compute from the byte count.
 *
 * Both measured 2026-09-18, see ~/Documents/me/noknok/voice/HAMSA-FINDINGS.md.
 */

const HEADER_BYTES = 44;

/**
 * Wrap signed 16-bit little-endian mono PCM in a RIFF header with real sizes.
 * Takes the raw bytes (server) or the samples (browser capture); no Node APIs,
 * so the browser imports it through `@repo/voice/wav`.
 */
export function toWav(
	samples: Uint8Array | Int16Array,
	sampleRate = 16_000,
): Uint8Array<ArrayBuffer> {
	const pcm = new Uint8Array(
		samples.buffer,
		samples.byteOffset,
		samples.byteLength,
	);
	const out = new Uint8Array(HEADER_BYTES + pcm.length);
	const view = new DataView(out.buffer);
	const ascii = (offset: number, text: string) => {
		for (let i = 0; i < text.length; i++) {
			view.setUint8(offset + i, text.charCodeAt(i));
		}
	};
	ascii(0, "RIFF");
	view.setUint32(4, 36 + pcm.length, true);
	ascii(8, "WAVEfmt ");
	view.setUint32(16, 16, true); // fmt chunk size
	view.setUint16(20, 1, true); // PCM
	view.setUint16(22, 1, true); // mono
	view.setUint32(24, sampleRate, true);
	view.setUint32(28, sampleRate * 2, true); // byte rate
	view.setUint16(32, 2, true); // block align
	view.setUint16(34, 16, true); // bits per sample
	ascii(36, "data");
	view.setUint32(40, pcm.length, true);
	out.set(pcm, HEADER_BYTES);
	return out;
}

/** The 16-bit samples of a WAV from `toWav`, copied: a socket's buffer need not be 2-byte aligned. */
export function wavSamples(wav: Uint8Array): Int16Array {
	const bytes = wav.subarray(HEADER_BYTES);
	const samples = new Int16Array(Math.floor(bytes.length / 2));
	new Uint8Array(samples.buffer).set(bytes.subarray(0, samples.length * 2));
	return samples;
}

/**
 * Duration from the byte count, ignoring the declared sizes.
 * Returns 0 for anything too short to hold audio.
 */
export function wavDurationSeconds(wav: Uint8Array, sampleRate = 16_000) {
	const bytes = wav.length - HEADER_BYTES;
	return bytes <= 0 ? 0 : bytes / 2 / sampleRate;
}
